import { useState, useRef, useEffect } from 'react'
import { MapPinIcon, GlobeAltIcon } from '@heroicons/react/24/outline'
import { Loader } from '@googlemaps/js-api-loader'

interface LocationAutocompleteProps {
  value: string | undefined
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

const LocationAutocomplete = ({ value, onChange, placeholder, className }: LocationAutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false)
  const [debugInfo, setDebugInfo] = useState<string>('')
  const [suggestionsHeight, setSuggestionsHeight] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null)
  const placesService = useRef<google.maps.places.PlacesService | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Initialize Google Places API
  useEffect(() => {
    const initializeGooglePlaces = async () => {
      try {
        // Get API key from environment variables
        const apiKey = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY

        if (!apiKey) {
          console.warn('Google Maps API key not found. Please set VITE_GOOGLE_MAPS_API_KEY in your environment variables.')
          setDebugInfo('No API key found')
          setIsGoogleMapsReady(false)
          return
        }

        setDebugInfo('Loading Google Maps API...')

        // Initialize Google Maps loader
        const loader = new Loader({
          apiKey: apiKey,
          version: 'weekly',
          libraries: ['places']
        })

        await loader.load()
        
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          autocompleteService.current = new google.maps.places.AutocompleteService()
          
          // Create a dummy div for PlacesService (required by Google Maps API)
          const dummyDiv = document.createElement('div')
          placesService.current = new google.maps.places.PlacesService(dummyDiv)
          
          setIsGoogleMapsReady(true)
          setDebugInfo('Google Maps API ready')
          console.log('Google Maps API initialized successfully')
        }
      } catch (error) {
        console.error('Error initializing Google Places:', error)
        setDebugInfo(`Error: ${error}`)
        setIsGoogleMapsReady(false)
      }
    }

    initializeGooglePlaces()
  }, [])

  // Update suggestions height when suggestions change
  useEffect(() => {
    if (showSuggestions && suggestionsRef.current) {
      const height = suggestionsRef.current.scrollHeight
      setSuggestionsHeight(height)
    } else {
      setSuggestionsHeight(0)
    }
  }, [showSuggestions, suggestions])

  // Handle input change and fetch suggestions
  const handleInputChange = async (inputValue: string) => {
    onChange(inputValue)

    // Clear suggestions if input is too short
    if (!inputValue.trim() || inputValue.trim().length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    // Check if Google Maps is ready
    if (!autocompleteService.current || !isGoogleMapsReady) {
      console.log('Google Maps not ready yet')
      return
    }

    setIsLoading(true)
    console.log('Fetching suggestions for:', inputValue)

    try {
      const request: google.maps.places.AutocompletionRequest = {
        input: inputValue,
        types: ['establishment', 'geocode'],
        // Remove country restriction to test if that's causing issues
        // componentRestrictions: { country: 'us' }
      }

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false)
        
        console.log('Google Places API response:', { status, predictions })
        
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
          console.log('Setting suggestions:', predictions)
          setSuggestions(predictions)
          setShowSuggestions(true)
        } else {
          console.log('No suggestions or error:', status)
          setSuggestions([])
          setShowSuggestions(false)
        }
      })
    } catch (error) {
      console.error('Error fetching place predictions:', error)
      setIsLoading(false)
      setSuggestions([])
      setShowSuggestions(false)
    }
  }

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: google.maps.places.AutocompletePrediction) => {
    console.log('Selected suggestion:', suggestion.description)
    onChange(suggestion.description)
    setSuggestions([])
    setShowSuggestions(false)
    inputRef.current?.blur()
  }

  // Generate Google Maps URL
  const getGoogleMapsUrl = (address: string | undefined) => {
    if (!address?.trim()) return '#'
    const encodedAddress = encodeURIComponent(address)
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
  }

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle input focus
  const handleInputFocus = () => {
    // Show suggestions if we have them and there's text
    if (value?.trim() && suggestions.length > 0) {
      setShowSuggestions(true)
    }
  }

  return (
    <div>
      {/* Input container - stays in place */}
      <div ref={containerRef} className="relative">
        <div className="relative">
          <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            placeholder={placeholder || "Start typing an address..."}
            className={className || "w-full pl-10 pr-12 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"}
          />
          
          {/* Google Maps Link Button */}
          {value?.trim() && (
            <a
              href={getGoogleMapsUrl(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-blue-400 transition-colors"
              title="Open in Google Maps"
            >
              <GlobeAltIcon className="w-5 h-5" />
            </a>
          )}
        </div>

        {/* Debug info (remove in production) */}
        {debugInfo && !isGoogleMapsReady && (
          <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-blue-500/20 border border-blue-500/30 rounded-xl backdrop-blur-sm text-xs z-50">
            <p className="text-blue-400">Debug: {debugInfo}</p>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-white/10 border border-white/20 rounded-xl backdrop-blur-sm z-50">
            <div className="flex items-center space-x-2 text-gray-400">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">Searching locations...</span>
            </div>
          </div>
        )}

        {/* Suggestions dropdown - positioned relative to input */}
        {showSuggestions && suggestions.length > 0 && (
          <div 
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white/10 border border-white/20 rounded-xl backdrop-blur-sm z-50 shadow-lg"
          >
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.place_id}
                type="button"
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                className="w-full p-3 text-left hover:bg-white/20 transition-colors border-b border-white/10 last:border-b-0 focus:outline-none focus:bg-white/20"
              >
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {suggestion.structured_formatting.main_text}
                    </p>
                    <p className="text-gray-400 text-xs truncate">
                      {suggestion.structured_formatting.secondary_text}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Google Maps API status */}
        {!isGoogleMapsReady && !isLoading && (
          <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-xl backdrop-blur-sm">
            <p className="text-yellow-400 text-sm">
              Google Maps API not available. Address autocomplete is disabled.
            </p>
          </div>
        )}
      </div>

      {/* Spacer div to push content down when suggestions are visible - separate from input container */}
      <div 
        className="transition-all duration-300 ease-in-out"
        style={{ height: `${suggestionsHeight}px` }}
      />
    </div>
  )
}

export default LocationAutocomplete 